/** Map overlay for toggling source-layer visibility without re-fetching. */

import { memo, useEffect, useId, useMemo, useRef, useState } from "react";

import { useLanguageContext } from "../hooks/useLanguage";
import type { LayerVisibility, MapMode, RegionConfig } from "../types";

interface LayerControlsProps {
  config: RegionConfig;
  mode: MapMode;
  visibility: LayerVisibility;
  onToggle: (layer: string) => void;
}

export const LAYER_LABEL_KEYS: Record<string, string> = {
  species: "species_habitats",
  measures: "measures",
  waterways: "waterways",
  ffh_areas: "ffh_areas",
  nsg_areas: "nsg_areas",
  krebspest: "krebspest",
};

function LayerSwatch({
  layer,
  config,
}: {
  layer: string;
  config: RegionConfig;
}) {
  const layerConfig = config.base_layers[layer];
  if (!layerConfig) return null;

  if (layer === "waterways") {
    return (
      <span
        className="h-0 w-7 shrink-0 rounded-full border-t-[3px]"
        style={{ borderColor: layerConfig.color }}
        aria-hidden="true"
      />
    );
  }

  return (
    <span
      className="h-4 w-4 shrink-0 rounded-sm border"
      style={{
        backgroundColor: layerConfig.color,
        borderColor: layerConfig.color,
        opacity: Math.max(layerConfig.opacity, 0.45),
      }}
      aria-hidden="true"
    />
  );
}

export const LayerControls = memo(function LayerControls({
  config,
  mode,
  visibility,
  onToggle,
}: LayerControlsProps) {
  const { t } = useLanguageContext();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const layers = useMemo(
    () => [
      ...(mode === "habitats" ? ["species"] : ["measures"]),
      ...Object.keys(config.base_layers),
    ],
    [config.base_layers, mode],
  );

  // Close the mobile popover on outside pointer / Escape.
  useEffect(() => {
    if (!open) return;
    const handlePointerOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handlePointerOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      data-tour-id="layers-control"
      className="absolute right-2 top-2 z-20 flex flex-col items-end sm:right-4 sm:top-4"
    >
      {/* Mobile trigger — collapses the panel into a compact icon button. */}
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t("layers")}
        className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/70 bg-white/95 text-flima-slate shadow-float backdrop-blur transition-colors hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river sm:hidden motion-reduce:transition-none"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="m12 2 9 5-9 5-9-5 9-5Z" />
          <path d="m3 12 9 5 9-5M3 17l9 5 9-5" />
        </svg>
      </button>

      <fieldset
        id={panelId}
        className={`${
          open ? "flex" : "hidden"
        } mt-2 max-h-[calc(100dvh-5rem)] w-[min(13rem,calc(100vw-1.5rem))] flex-col overflow-y-auto overscroll-contain rounded-xl border border-white/70 bg-white/95 p-3 shadow-float backdrop-blur sm:mt-0 sm:flex sm:w-52`}
      >
        <legend className="ml-1 rounded-full bg-flima-spruce px-3 py-1 font-utility text-[0.62rem] uppercase tracking-[0.14em] text-white">
          {t("layers")}
        </legend>
        <div className="mt-1 space-y-0.5">
          {layers.map((layer) => (
            <label
              key={layer}
              className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-flima-mist focus-within:bg-flima-mist motion-reduce:transition-none"
            >
              <input
                type="checkbox"
                checked={visibility[layer] !== false}
                onChange={() => onToggle(layer)}
                className="h-4 w-4 shrink-0 rounded border-flima-spruce/25 [accent-color:#147D92] focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2"
              />
              <span className="min-w-0 flex-1 break-words text-xs font-semibold leading-tight text-flima-slate/80">
                {t(LAYER_LABEL_KEYS[layer] || layer)}
              </span>
              <LayerSwatch layer={layer} config={config} />
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
});
